import { Question, QuestionCore, QuestionType, ModuleId } from '../types'
import { MODULES } from '../modules'
import {
  SEED_COLLECTIONS,
  getAllSeedQuestions,
  SeedQuestion,
  getAllQuestionsWithSources as getAllSeedQuestionsWithSources,
} from './seedConsolidator'
import { error as logError, debug, group, groupEnd } from '../../utils/logger'
import {
  assertCanonicalSeedFeedback,
  isCitoEligible,
  validateQuestion,
} from './questionValidation'

// Re-export from split modules for backward compatibility
export {
  fisherYatesShuffle,
  shuffleQuestionOptions,
  shuffleQuestionWithExplanations,
} from './questionShuffling'
export {
  assertCanonicalSeedFeedback,
  isCitoEligible,
  validateQuestion,
} from './questionValidation'
export {
  getCitoQuestions,
  getCitoQuestionsBySkill,
  getCitoSkillSummary,
} from './citoSelection'

/**
 * Question Loader — Taxonomy-Authoritative
 *
 * Single source of truth: 8 module-based seed collections.
 * Tier is authoritative.
 * All questions have required taxonomy fields.
 *
 * Part of the Content domain (DDD Fase 3).
 * Moved from src/data/questionLoader.ts in R27.
 * Split into 4 modules in R38: questionLoader (core), questionShuffling,
 * questionValidation, citoSelection.
 */

// ============================================================
// Seed → QuestionCore Conversion
// ============================================================

/**
 * Convert a SeedQuestion to Question (the public question type).
 */
function seedToCore(seed: SeedQuestion): Question {
  assertCanonicalSeedFeedback(seed)
  return {
    id: seed.id,
    module: seed.module,
    question: seed.question,
    questionType: seed.questionType as QuestionType,
    citoDifficulty: seed.citoDifficulty,
    citoDiscrimination: seed.citoDiscrimination,
    skill: seed.skill,
    subSkill: seed.subSkill,
    familyId: seed.familyId,
    context: seed.context,
    options: seed.options,
    correctAnswer: seed.correctAnswer,
    isAnchor: seed.isAnchor,
    data: seed.data,
    feedback: seed.feedback,
    stimulusAids: seed.stimulusAids,
    supportAids: seed.supportAids,
    // Taxonomy-authoritative fields (tier is canonical)
    tier: seed.tier,
    levelCode: seed.levelCode,
    group: seed.group,
    conceptId: seed.conceptId,
    variantIndex: seed.variantIndex,
  }
}

// ============================================================
// Build Core Question Arrays
// ============================================================

const ALL_SEEDS = getAllSeedQuestions()

/** All questions as Question (normalized, without explanations) */
export const ALL_QUESTIONS_CORE: Question[] = ALL_SEEDS.map(seedToCore)

/**
 * Stable signal that increments when the authoritative question corpus changes.
 * Consumers can depend on this to refresh memoized projections without relying on
 * referential identity of `getAllQuestions()` results.
 */
export const QUESTION_DATA_VERSION = ALL_QUESTIONS_CORE.map(
  (question) => question.id,
).join('|')

/** Explanation data indexed by question ID */
const ALL_EXPLANATIONS: Record<
  string,
  {
    feedback?: Question['feedback']
    stimulusAids?: Question['stimulusAids']
    supportAids?: Question['supportAids']
  }
> = {}
ALL_SEEDS.forEach((seed) => {
  try {
    assertCanonicalSeedFeedback(seed)
  } catch (err) {
    logError(`Canonical feedback assertion failed for ${seed.id}:`, err)
  }
  const feedback = seed.feedback as Question['feedback']
  const stimulusAids = seed.stimulusAids as Question['stimulusAids']
  const supportAids = seed.supportAids as Question['supportAids']
  if (feedback || stimulusAids || supportAids) {
    ALL_EXPLANATIONS[seed.id] = {
      feedback,
      stimulusAids,
      supportAids,
    }
  }
})

// ============================================================
// PRE-BUILT INDEXES (Performance Optimization)
// ============================================================

/** Lookup a single question by ID */
export const QUESTION_BY_ID: Map<string, Question> = new Map(
  ALL_QUESTIONS_CORE.map((q) => [q.id, q]),
)

/** All questions for a given module */
export const QUESTIONS_BY_MODULE: Map<ModuleId, Question[]> = new Map()

/** Questions for a module+subSkill combination */
export const QUESTIONS_BY_MODULE_SUBSKILL: Map<string, Question[]> = new Map()

/** Questions grouped by familyId */
export const QUESTIONS_BY_FAMILY: Map<string, Question[]> = new Map()

/** All unique subSkills per module */
export const SUBSKILLS_BY_MODULE: Map<ModuleId, Set<string>> = new Map()

/** Questions by module+tier (authoritative index) */
export const QUESTIONS_BY_MODULE_TIER: Map<string, Question[]> = new Map()

// Build all indexes in a single pass
ALL_QUESTIONS_CORE.forEach((q) => {
  // By module
  if (!QUESTIONS_BY_MODULE.has(q.module)) {
    QUESTIONS_BY_MODULE.set(q.module, [])
  }
  QUESTIONS_BY_MODULE.get(q.module)!.push(q)

  // By module+tier (authoritative)
  const mtKey = `${q.module}-${q.tier}`
  if (!QUESTIONS_BY_MODULE_TIER.has(mtKey)) {
    QUESTIONS_BY_MODULE_TIER.set(mtKey, [])
  }
  QUESTIONS_BY_MODULE_TIER.get(mtKey)!.push(q)

  // By module+subSkill
  const msKey = `${q.module}-${q.subSkill}`
  if (!QUESTIONS_BY_MODULE_SUBSKILL.has(msKey)) {
    QUESTIONS_BY_MODULE_SUBSKILL.set(msKey, [])
  }
  QUESTIONS_BY_MODULE_SUBSKILL.get(msKey)!.push(q)

  // SubSkills per module
  if (!SUBSKILLS_BY_MODULE.has(q.module)) {
    SUBSKILLS_BY_MODULE.set(q.module, new Set())
  }
  SUBSKILLS_BY_MODULE.get(q.module)!.add(q.subSkill)

  // By familyId
  if (!QUESTIONS_BY_FAMILY.has(q.familyId)) {
    QUESTIONS_BY_FAMILY.set(q.familyId, [])
  }
  QUESTIONS_BY_FAMILY.get(q.familyId)!.push(q)
})

// ============================================================
// Index Query Helpers
// ============================================================

/** Get questions for a module (indexed) */
export function getModuleQuestions(moduleId: ModuleId): Question[] {
  return QUESTIONS_BY_MODULE.get(moduleId) || []
}

/** Get questions for a module+tier (indexed, authoritative) */
export function getModuleTierQuestions(
  moduleId: ModuleId,
  tier: number,
): Question[] {
  return QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || []
}

/** Get questions for a module+subSkill (indexed) */
export function getModuleSubSkillQuestions(
  moduleId: ModuleId,
  subSkill: string,
): Question[] {
  return QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || []
}

/** Get questions for a module+subSkill+tier (indexed, composed) */
export function getModuleSubSkillTierQuestions(
  moduleId: ModuleId,
  subSkill: string,
  tier: number,
): Question[] {
  const subSkillQuestions =
    QUESTIONS_BY_MODULE_SUBSKILL.get(`${moduleId}-${subSkill}`) || []
  return subSkillQuestions.filter((q) => q.tier === tier)
}

// CITO-eligible questions subset
export const CITO_ELIGIBLE_QUESTIONS: Question[] =
  ALL_QUESTIONS_CORE.filter(isCitoEligible)

// ============================================================
// Question Retrieval (with explanations)
// ============================================================

/**
 * Get a single question with its explanation.
 */
export function getQuestionWithExplanation(
  questionId: string,
): Question | null {
  try {
    const core = QUESTION_BY_ID.get(questionId)
    if (!core) {
      logError(`QUESTION ERROR: Question not found: ${questionId}`)
      return null
    }
    const explanation = ALL_EXPLANATIONS[questionId]
    if (!explanation) {
      return {
        ...core,
        feedback: {
          explanationText: 'Uitleg wordt binnenkort toegevoegd.',
          distractorExplanations: {},
        },
      }
    }

    // Return question with explanation data directly
    return {
      ...core,
      feedback: explanation.feedback,
      stimulusAids: explanation.stimulusAids,
      supportAids: explanation.supportAids,
    }
  } catch (error) {
    logError('Error in getQuestionWithExplanation:', error, questionId)
    return null
  }
}

/**
 * Get all questions for a module (with explanations).
 */
export function getQuestionsByModule(moduleId: ModuleId): Question[] {
  try {
    const questions = (QUESTIONS_BY_MODULE.get(moduleId) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
    return questions
  } catch (error) {
    logError('Error in getQuestionsByModule:', error, moduleId)
    return []
  }
}

// ============================================================
// Full Question Retrieval
// ============================================================

/**
 * Get all questions (with explanations).
 */
export function getAllQuestions(): Question[] {
  try {
    return ALL_QUESTIONS_CORE.map((q) =>
      getQuestionWithExplanation(q.id),
    ).filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getAllQuestions:', error)
    return []
  }
}

/**
 * Get questions by type (for special rendering).
 */
export function getQuestionsByType(type: string): Question[] {
  try {
    return ALL_QUESTIONS_CORE.filter((q) => q.questionType === type)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsByType:', error)
    return []
  }
}

/**
 * Validate all questions and explanations.
 */
export function validateQuestionsAndExplanations(): {
  valid: boolean
  errors: string[]
  warnings: string[]
} {
  const errors: string[] = []
  const warnings: string[] = []
  ALL_QUESTIONS_CORE.forEach((question) => {
    // Taxonomy validation (strict)
    const taxonomyErrors = validateQuestion(question)
    errors.push(...taxonomyErrors)

    // Content validation
    if (!question.question)
      errors.push(`Question ${question.id} missing question text`)

    // Options and correctAnswer validation — only for MC questions
    if (question.questionType === 'multiple_choice') {
      if (!question.options || question.options.length === 0) {
        errors.push(`Question ${question.id} missing options`)
      }
      if (
        question.correctAnswer === undefined ||
        question.correctAnswer === null
      ) {
        errors.push(`Question ${question.id} missing correctAnswer`)
      }
    }

    const explanation = ALL_EXPLANATIONS[question.id]
    if (!explanation) {
      warnings.push(`Question ${question.id} missing explanation`)
    }
  })
  return {
    valid: errors.length === 0,
    errors,
    warnings,
  }
}

// ============================================================
// Tier-Based Retrieval (Authoritative)
// ============================================================

/**
 * Get questions for a specific module and tier.
 */
export function getQuestionsForTier(
  moduleId: ModuleId,
  tier: number,
): Question[] {
  try {
    return (QUESTIONS_BY_MODULE_TIER.get(`${moduleId}-${tier}`) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForTier:', error)
    return []
  }
}

/**
 * Get questions for a specific familyId.
 */
export function getQuestionsForFamily(familyId: string): Question[] {
  try {
    return (QUESTIONS_BY_FAMILY.get(familyId) || [])
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForFamily:', error)
    return []
  }
}

/**
 * Get questions for a familyId within a tier range.
 */
export function getQuestionsForFamilyInTierRange(
  familyId: string,
  minTier: number,
  maxTier: number,
): Question[] {
  try {
    return (QUESTIONS_BY_FAMILY.get(familyId) || [])
      .filter((q) => q.tier >= minTier && q.tier <= maxTier)
      .map((q) => getQuestionWithExplanation(q.id))
      .filter((q): q is Question => q !== null)
  } catch (error) {
    logError('Error in getQuestionsForFamilyInTierRange:', error)
    return []
  }
}

// ============================================================
// Seed Collection Stats
// ============================================================

/** Per-module stats shape */
interface ModuleStats {
  count: number
  families: number
  tierRange: [number, number]
  questionTypes: string[]
}

/**
 * Get statistics about the seed collections.
 */
export function getSeedStats(): {
  totalQuestions: number
  byModule: Record<string, ModuleStats>
} {
  const byModule: Record<string, ModuleStats> = {}
  for (const [moduleId, collection] of SEED_COLLECTIONS) {
    const families = new Set<string>()
    const tiers = new Set<number>()
    const types = new Set<string>()
    for (const q of collection.questions) {
      families.add(q.familyId)
      tiers.add(q.tier)
      types.add(q.questionType)
    }
    byModule[moduleId] = {
      count: collection.questions.length,
      families: families.size,
      tierRange:
        tiers.size > 0 ? [Math.min(...tiers), Math.max(...tiers)] : [0, 0],
      questionTypes: Array.from(types).sort(),
    }
  }
  return {
    totalQuestions: ALL_QUESTIONS_CORE.length,
    byModule,
  }
}

// ============================================================
// Exports
// ============================================================

export { MODULES }

// ============================================================
// Development Logging
// ============================================================

if (process.env.NODE_ENV === 'development') {
  try {
    const allErrors = ALL_QUESTIONS_CORE.flatMap(validateQuestion)
    if (allErrors.length > 0) {
      group('❌ Question Taxonomy Validation Errors')
      allErrors.slice(0, 30).forEach((error) => logError(error))
      if (allErrors.length > 30) {
        logError(`... and ${allErrors.length - 30} more errors`)
      }
      groupEnd()
    }
    const stats = getSeedStats()
    debug(
      `✅ Question Loader v4.1: ${stats.totalQuestions} questions across ${Object.keys(stats.byModule).length} modules ` +
        `(${CITO_ELIGIBLE_QUESTIONS.length} CITO-eligible)`,
    )
  } catch (err) {
    logError('Question validation failed during module init:', err)
  }
}

// ============================================================
// Diagnostics
// ============================================================

export interface QuestionWithSourceDiagnostics extends Question {
  questionId: string
  moduleCode: string
  __sourceFile: string
  stimulusAids?: Question['stimulusAids']
  supportAids?: Question['supportAids']
}

/**
 * Returns runtime-loaded questions with provenance for diagnostics.
 */
export function getAllQuestionsWithSourcesForDiagnostics(): QuestionWithSourceDiagnostics[] {
  return getAllSeedQuestionsWithSources().map((seed) => {
    const core = seedToCore(seed)
    return {
      ...core,
      questionId: core.id,
      moduleCode: seed.module
        ? (SEED_COLLECTIONS.get(seed.module)?.moduleCode ?? '')
        : '',
      __sourceFile: seed.__sourceFile ?? 'unknown:source-file-not-available',
      stimulusAids: core.stimulusAids,
      supportAids: core.supportAids,
    }
  })
}
